import React from "react";
import { genericMapStateToProps, IGenericUserProps } from "../../DataClasses/GenericUser";
import idssStyle from "../../Configuration/SharedStyling";
import { connect } from "react-redux";
import { Theme, createStyles, } from '@mui/material/styles';;
import { WithStyles, withStyles } from '@mui/styles';;
import { Redirect } from "react-router-dom";
import AdministrationService from "../Services/AdministrationService";
import { AjaxError } from "rxjs/ajax";
import { firstValueFrom } from "rxjs";
import { Fab, Grid2 as Grid, IconButton, Tooltip } from "@mui/material";
import SharedMaterialReactTable, { ISharedTableColumn } from "../Shared/MaterialReactTable";
import { RemoveCircle, Delete } from "@mui/icons-material";
import GetAppIcon from '@mui/icons-material/GetApp';
import saveAs from 'file-saver';
import { ICachingKey } from "../../DataClasses/AdministrationData";

const styles = (theme: Theme) => createStyles({ ...idssStyle(theme) });

interface ICacheManagementStateStore {
    isLoading: boolean;
    redirect: boolean;
    data: ICachingKey[];
    isActing: boolean;
    isClearingAllCache: boolean;
}

type IStylesProps = WithStyles<typeof styles>;

class CacheManagementView extends React.Component<
    IGenericUserProps & IStylesProps,
    ICacheManagementStateStore
> {
    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: true,
            redirect: false,
            data: [],
            isActing: false,
            isClearingAllCache: false
        }
    }

    // Define table columns
    private getTableColumns = (): ISharedTableColumn<ICachingKey>[] => [
        {
            accessorKey: 'key',
            header: 'Key',
            enableSorting: true,
            enableFiltering: true,
        },
        {
            accessorKey: 'expireAfterMilliseconds',
            header: 'Expire On',
            enableSorting: true,
            enableFiltering: false,
            Cell: ({ row }) => this.getLocalExpireTime(row.original.expireAfterMilliseconds),
        },
        {
            accessorKey: 'key' as keyof ICachingKey,
            header: 'Download',
            enableSorting: false,
            enableFiltering: false,
            enableColumnActions: false,
            Cell: ({ row }) => this.downloadBodyTemplate(row.original),
        },
        {
            accessorKey: 'key' as keyof ICachingKey,
            header: 'Clear Cache',
            enableSorting: false,
            enableFiltering: false,
            enableColumnActions: false,
            Cell: ({ row }) => this.clearCacheBodyTemplate(row.original),
        },
    ];

    public componentDidMount() {
        this.setState({ isLoading: true });
        this.RefreshModel();
        this.setState({ isLoading: false });
    }

    private redirectToErrorPage() {
        return <Redirect to='/errorpage' />;
    }

    private downloadBodyTemplate = (rowData: ICachingKey) => {
        return (
            <Tooltip title="Download" aria-label="Download">
                <IconButton size='medium' aria-label="Download" onClick={() => this.downloadCache(rowData.key)} disabled={this.state.isClearingAllCache || this.state.isActing}>
                    <GetAppIcon fontSize="inherit"></GetAppIcon>
                </IconButton >
            </Tooltip>
        );
    };

    private clearCacheBodyTemplate = (rowData: ICachingKey) => {
        return (
            <Tooltip title="Clear Cache" aria-label="Clear Cache">
                <IconButton size='medium' onClick={async () => await this.clearCache(rowData.key)} disabled={this.state.isClearingAllCache || this.state.isActing}>
                    <Delete fontSize="inherit"></Delete>
                </IconButton >
            </Tooltip>
        );
    };

    private tableHeader = () => {
        const { classes } = this.props;
        return (
            <Fab className={classes.fab} variant="extended" color="primary" size="small" onClick={async () =>
                await this.clearAllCache()} disabled={this.state.isClearingAllCache || this.state.isActing}>
                <RemoveCircle className={classes.extendedIcon} />
                {this.state.isClearingAllCache ? "Clearing Cache..." : "Clear All Cache"}
            </Fab>
        );
    };

    public render() {
        if (this.state.redirect) {
            return this.redirectToErrorPage();
        }
        return (
            <div>
                <Grid size={6}>
                    <SharedMaterialReactTable
                        data={this.state.data}
                        columns={this.getTableColumns()}
                        title=""
                        isLoading={this.state.isLoading}
                        enablePagination={true}
                        enableSorting={true}
                        enableFiltering={true}
                        enableColumnActions={true}
                        enableRowActions={false}
                        renderTopToolbarCustomActions={() => this.tableHeader()}
                        initialState={{
                            pagination: { pageIndex: 0, pageSize: 8 },
                        }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                        }}
                        emptyMessage="No cache keys found."
                    />
                </Grid>
            </div>
        );
    }

    private async clearAllCache() {
        this.setState({ isClearingAllCache: true });
        const keysCsv = this.state.data.map(x => x.key).join(',');
        await this.clearCache(keysCsv);
        alert("All the Metadata cache cleared!");
        this.RefreshModel();
        this.setState({ isClearingAllCache: false });
    }
    private async clearCache(cacheKeysCsv: string) {
        this.setState({ isActing: true });
        await firstValueFrom(
            AdministrationService.ClearCaches$(cacheKeysCsv, this.props.user)
        );
        this.RefreshModel();
        this.setState({ isActing: false });
    }
    private getLocalExpireTime(expireAfterMilliseconds: number) {
        if (expireAfterMilliseconds < 1) {
            return 'N/A';
        }
        const now = Date.now();
        const later = now + expireAfterMilliseconds;
        const date = new Date(later);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');  // Months are 0-based, so +1 is needed
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    private async RefreshModel() {
        const retval = await firstValueFrom(
            AdministrationService.GetMetadataCacheList$(this.props.user)
        );
        if (retval instanceof AjaxError || !retval) {
            this.setRedirectState();
        } else {
            this.setState({ data: retval });
        }
    }

    private setRedirectState() {
        this.setState({ redirect: true });
    }

    private downloadCache = async (key: string) => {
        this.setState({ isActing: true });
        const apiCall = AdministrationService.DownloadCache$(key, this.props.user);

        try {
            const userResponse = await firstValueFrom(apiCall);
            const fileName = key + new Date().toLocaleString() + ".json";
            if (typeof userResponse === 'string') {
                saveAs(new Blob([userResponse], {
                    type: "text/plain"
                }), fileName);
            }
        }
        catch (ex) {
            // handle error
        }
        finally {
            this.setState({ isActing: false });
        }
    }
}

// REDUX CONTAINER
const CacheManagementPageWithState = connect<IGenericUserProps>(
    genericMapStateToProps
)(CacheManagementView);

// STYLES CONTAINER
const CacheManagementPage = withStyles(styles)(
    CacheManagementPageWithState
);

export default CacheManagementPage;
